import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AppProvider } from "./context/AppContext";
import { AdminProvider, useAdmin } from "./context/AdminContext";

// Store Pages
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmationPage, { CheckoutSuccessPage, CheckoutCancelPage } from "./pages/OrderConfirmationPage";
import { LoginPage, RegisterPage } from "./pages/AuthPages";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ContactPage from "./pages/ContactPage";
import { AboutPage, PrivacyPage, TermsPage } from "./pages/StaticPages";
import FavoritesPage from "./pages/FavoritesPage";
import GenericPage from "./pages/GenericPage";
import ReturnFormPage from "./pages/ReturnFormPage";

// Admin Components
import AdminLogin from "./components/admin/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./components/admin/AdminDashboard";
import AdminProducts from "./components/admin/AdminProducts";
import AdminCategories from "./components/admin/AdminCategories";
import AdminOrders from "./components/admin/AdminOrders";
import AdminSettings from "./components/admin/AdminSettings";
import { AdminCustomers, AdminPayments, AdminReviews } from "./components/admin/AdminOtherPages";
import { AdminPages, AdminBlog, AdminSliders, AdminCoupons, AdminFooterPages } from "./components/admin/AdminCMS";
import AdminLoyalty from "./components/admin/AdminLoyalty";
import AdminReferrals from "./components/admin/AdminReferrals";
import AdminAnalytics from "./components/admin/AdminAnalytics";
import AdminMedia from "./components/admin/AdminMedia";

// Protected Admin Route
const ProtectedAdminRoute = ({ children }) => {
  const { admin, loading } = useAdmin();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!admin) {
    return <Navigate to="/rani-admin/login" replace />;
  }
  
  return children;
};

// Admin Routes Wrapper
const AdminRoutes = () => {
  return (
    <AdminProvider>
      <Routes>
        <Route path="login" element={<AdminLogin />} />
        <Route
          path="/*"
          element={
            <ProtectedAdminRoute>
              <AdminLayout />
            </ProtectedAdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="loyalty" element={<AdminLoyalty />} />
          <Route path="referrals" element={<AdminReferrals />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="pages" element={<AdminPages />} />
          <Route path="footer-pages" element={<AdminFooterPages />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="sliders" element={<AdminSliders />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
      </Routes>
    </AdminProvider>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Admin Routes */}
            <Route path="/rani-admin/*" element={<AdminRoutes />} />
            
            {/* Store Routes */}
            <Route
              path="/*"
              element={
                <AppProvider>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/shop" element={<ShopPage />} />
                    <Route path="/product/:productId" element={<ProductPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                    <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
                    <Route path="/order-confirmation/:orderId" element={<OrderConfirmationPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/favorites" element={<FavoritesPage />} />
                    <Route path="/search" element={<ShopPage />} />
                    <Route path="/formular-de-retur" element={<ReturnFormPage />} />
                    <Route path="/page/:slug" element={<GenericPage />} />
                  </Routes>
                </AppProvider>
              }
            />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
